// Authentication utilities for Azure AD integration
// Authentication utilities for Azure AD integration

import { AccountInfo, AuthenticationResult, IPublicClientApplication } from '@azure/msal-browser'
import { apiClient } from './api'

export interface AuthState {
  isAuthenticated: boolean
  user?: AccountInfo
  token?: string
  error?: string
}

const AUTH_STORAGE_KEY = 'auth_state'

class AuthService {
  private msalInstance?: IPublicClientApplication
  private initializationPromise?: Promise<void>
  private onStateChange?: (state: AuthState) => void

  constructor() {
    this.initializationPromise = this.initializeMsal()
  }

  // localStorage methods
  private getAuthState(): AuthState {
    try {
      const stored = localStorage.getItem(AUTH_STORAGE_KEY)
      if (stored) {
        return JSON.parse(stored)
      }
    } catch (error) {
      console.error('Failed to load auth state from localStorage:', error)
    }
    return { isAuthenticated: false }
  }

  private setAuthState(state: AuthState): void {
    try {
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(state))
      
      // Trigger state change callback for React re-renders
      if (this.onStateChange) {
        this.onStateChange(state)
      }
    } catch (error) {
      console.error('Failed to save auth state to localStorage:', error)
    }
  }

  private clearAuthState(): void {
    try {
      localStorage.removeItem(AUTH_STORAGE_KEY)
      const emptyState = { isAuthenticated: false }
      if (this.onStateChange) {
        this.onStateChange(emptyState)
      }
    } catch (error) {
      console.error('Failed to clear auth state from localStorage:', error)
    }
  }

  setMsalInstance(instance: IPublicClientApplication) {
    this.msalInstance = instance
  }

  setOnStateChange(callback: (state: AuthState) => void) {
    this.onStateChange = callback
  }

  private async initializeMsal() {
    try {
      const config = await apiClient.getAuthConfig()
      
      if (!config.auth_enabled) {
        throw new Error('Authentication is required but not enabled')
      }

      if (!config.aad_tenant_id || !config.aad_client_id) {
        console.warn('Azure AD configuration is incomplete')
        return
      }

      // Check if we have a stored token and set it in API client
      const authState = this.getAuthState()
      if (authState.token) {
        apiClient.setAuthToken(authState.token)
      }

      // Handle redirect results for authorization code flow
      await this.handleRedirectResult()
    } catch (error) {
      console.error('Failed to initialize auth:', error)
      // Don't store error in localStorage, just log it
    }
  }

  async waitForInitialization(): Promise<void> {
    if (this.initializationPromise) {
      await this.initializationPromise
    }
  }

  async login(): Promise<void> {
    try {
      
      // Redirect directly to backend which will handle the OAuth flow
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000'
      window.location.href = `${apiUrl}/api/auth/login`
      
    } catch (error) {
      console.error('Login failed:', error)
      const errorState: AuthState = {
        isAuthenticated: false,
        error: 'Login failed'
      }
      this.setAuthState(errorState)
      throw error
    }
  }

  async logout(): Promise<void> {
    try {
      // Clear localStorage and auth state
      this.clearAuthState()
      
      // Clear API client token
      apiClient.clearAuthToken()
      
      // Redirect to login page
      window.location.href = '/login'
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  async getToken(): Promise<string | null> {
    // Return the token from localStorage
    const authState = this.getAuthState()
    return authState.token || null
  }

  private async handleRedirectResult(): Promise<void> {
    
    try {
      // Check if we have a token from backend redirect
      const urlParams = new URLSearchParams(window.location.search)
      const token = urlParams.get('token')
      const error = urlParams.get('error')
      
      
      if (token) {
        
        // Save auth state to localStorage
        const newAuthState: AuthState = {
          isAuthenticated: true,
          token: token,
        }
        
        this.setAuthState(newAuthState)
        
        
        // Set token in API client
        apiClient.setAuthToken(token)
        
        // The /auth/success route will automatically redirect to home
        return
      }
      
      if (error) {
        // Store error in localStorage temporarily
        const errorState: AuthState = {
          isAuthenticated: false,
          error: `Authentication failed: ${error}`
        }
        this.setAuthState(errorState)
        return
      }

      // Skip the regular MSAL flow - we only use authorization code exchange now
    } catch (error) {
      console.error('Failed to handle redirect result:', error)
    }
  }



  getAuthStateForApp(): AuthState {
    return this.getAuthState()
  }

  isAuthenticated(): boolean {
    const authState = this.getAuthState()
    return authState.isAuthenticated
  }

  getUser(): AccountInfo | undefined {
    const authState = this.getAuthState()
    return authState.user
  }
}

// Export singleton instance
export const authService = new AuthService()

// Export class for testing
export { AuthService }

